import { HTTP_STATUS } from 'affiny-core/helpers/AsyncStatusHandler';
import { parseApiDate } from 'affiny-core/helpers/date';
import { IMessage } from 'affiny-core/modules/messages/types';
import { IRootState } from 'affiny-core/modules/state';
import { selectMember } from 'affiny-core/redux/selectors/membersSelectors';
import {
  selectLastMessage,
  selectMessage,
} from 'affiny-core/redux/selectors/messagesSelectors';
import orderBy from 'lodash/orderBy';

/* ***** *****  Utils  ***** ***** */

const selectMessagesIdsByOtherMemberId = (
  state: IRootState,
  otherAccountId: string
): string[] => {
  if (!otherAccountId) return [];

  const threadId = selectThreadIdByOtherAccountId(state, otherAccountId) || '';

  return selectMessageIdsByThreadId(state, threadId);
};

const selectThreadHasOneMessage = (
  state: IRootState,
  accountId: string
): boolean => {
  const messageIds = selectMessagesIdsByOtherMemberId(state, accountId);

  return messageIds.length === 1;
};

const selectIsFirstMessageFromMe = (
  state: IRootState,
  accountId: string
): boolean => {
  const messageIds = selectMessagesIdsByOtherMemberId(state, accountId);

  if (messageIds.length === 0) return false;

  const firstMessage = selectMessage(state, messageIds[0]);

  return firstMessage.attributes.isFromMe;
};

/* ***** *****  Threads  ***** ***** */

export const selectThreadByThreadId = (state: IRootState, threadId: string) =>
  state.threads.entries[threadId];

export const selectThreadIdByOtherAccountId = (
  state: IRootState,
  accountId: string
) =>
  (state.threads.list || []).find(
    id => state.threads?.entries[id].relationships.accountId === accountId
  );

export const selectThreadByOtherAccountId = (
  state: IRootState,
  accountId: string
) => {
  const threadId = selectThreadIdByOtherAccountId(state, accountId);

  if (!threadId) return null;

  return state.threads.entries[threadId];
};

export const selectThreadsIdAndOtherAccountId = (state: IRootState) =>
  selectThreadsId(state).map(threadId => {
    const thread = selectThreadByThreadId(state, threadId);

    return {
      threadId: threadId,
      otherAccountId: thread.relationships.accountId,
    };
  });

export const selectThreadsId = (state: IRootState): string[] => {
  const nonBlacklistedThreads = state.threads.list.filter(
    (threadId: string) => {
      const member = selectMemberByThreadId(state, threadId);

      if (!member) return false;

      return !member.attributes.isBlacklisted;
    }
  );

  return orderBy(
    nonBlacklistedThreads,
    (threadId: string) => {
      const lastMessage = selectLastMessageByThreadId(state, threadId);

      if (!lastMessage) return;

      return parseApiDate(lastMessage.attributes.sentAt);
    },
    ['desc']
  );
};

/****** *****  Members ***** ***** */

export const selectMemberByThreadId = (state: IRootState, threadId: string) =>
  selectMember()(
    state,
    state.threads.entries[threadId]?.relationships.accountId
  );

/* ***** *****  Messages  ***** ***** */

export const selectLastMessageByThreadId = (
  state: IRootState,
  threadId: string
): IMessage | undefined => {
  const thread = selectThreadByThreadId(state, threadId);

  if (!thread || !thread.relationships) return;

  return selectLastMessage(state, thread.relationships.messages.list);
};

export const selectMessageIdsByThreadId = (
  state: IRootState,
  threadId: string
) => {
  const thread = selectThreadByThreadId(state, threadId);

  if (!thread || !thread.relationships) return [];

  return thread.relationships.messages.list;
};

export const selectMessagesByOtherMemberId = (
  state: IRootState,
  otherAccountId: string
) => {
  const messageIds = selectMessagesIdsByOtherMemberId(state, otherAccountId);

  return messageIds.map((messageId: string) => selectMessage(state, messageId));
};

export const selectLastMessageAttributesByThreadId = (
  state: IRootState,
  threadId?: string
) => {
  if (!threadId) return;

  const lastMessage = selectLastMessageByThreadId(state, threadId);

  if (!lastMessage || !lastMessage.attributes) return;

  return lastMessage.attributes;
};

export const selectShouldBeDrafted = (state: IRootState, threadId: string) => {
  const thread = selectThreadByThreadId(state, threadId);
  const member = selectMemberByThreadId(state, threadId);
  const isAllowUser = member ? !member.attributes.isBlacklisted : false;

  if (isAllowUser && !thread) return true;

  if (!thread?.relationships?.messages || !isAllowUser) {
    return;
  }

  return thread.relationships.messages.list.length === 0;
};

/* ***** *****  Meta  ***** ***** */

export const selectShouldShowMessage = (
  state: IRootState,
  accountId: string
): boolean =>
  selectIsFirstMessageFromMe(state, accountId) &&
  selectThreadHasOneMessage(state, accountId);

export const selectCurrentPage = (state: IRootState): number =>
  state.threads.meta.currentPage;

export const selectTotalItem = (state: IRootState): number | undefined =>
  state.threads.meta.total;

export const selectThreadCurrentPage = (
  state: IRootState,
  threadId: string
): number => {
  const thread = selectThreadByThreadId(state, threadId);

  if (!thread) return 0;

  // we used idx to prevent an error when relationships is undefined
  // we haven't found the root cause of this, so the typings were not updated
  return thread.relationships.messages.meta.current_page || 0;
};

export const selectThreadTotalItem = (
  state: IRootState,
  threadId: string
): number => {
  const thread = selectThreadByThreadId(state, threadId);

  if (!thread) return 0;

  return thread.relationships.messages.meta.total || 0;
};

// TODO do we want to let the user post message if he gets a 500 error?
export const selectCanPostMessage = (state: IRootState) => {
  const status = state.asynchronousState?.POST_DRY_RUN?.error?.status;

  return (
    status !== HTTP_STATUS.PAYMENT_REQUIRED &&
    status !== HTTP_STATUS.NOT_ACCEPTABLE &&
    status !== HTTP_STATUS.LOCKED
  );
};
